/**
 * 本将棋盤 (9x9) + 駒台 コンポーネント。
 *
 * - 盤の駒 click → from 選択 → to 候補ハイライト → click で着手。
 *   成れる場合は 成/不成 選択ダイアログ。
 * - 駒台の駒 click → 打ち駒モード → 空きマス候補ハイライト → click で `Drop`。
 * - yourSide が後手なら盤を 180° 反転 (Gote が下に来る)。
 *
 * ─────────────────────────────────────────────────────────
 * 【駒の向き仕様】 ※ ここを毎回読んでから触ること。 何度も間違えた。
 * ─────────────────────────────────────────────────────────
 *
 * (1) **将棋伝統の見せ方**: 敵 (= viewer から見て向こう側) の駒は
 *     **漢字も含めて要素ごと 180° 反転**。 オンライン将棋風の「漢字は両者正立」 は
 *     **やらない** (ユーザーから明確に否定された)。
 *
 * (2) 反転の判定は **viewer (= 観戦者の視点)** で動的に決める。
 *     **owner 固定で判定してはいけない** (yourSide='Second' で盤反転したとき
 *     自分の駒が逆さまになる)。
 *
 *     pieceFlipped = (cell.owner !== mySide)   // mySide = yourSide ?? 'First'
 *
 *   - viewer の **自分側 (= 画面下)** の駒 → 反転なし (正立)
 *   - viewer の **敵側 (= 画面上)** の駒   → 駒形も漢字も 180° 反転
 *   - 駒台も同じ規則: `handFlipped = (side !== mySide)`
 *
 * (3) 反転は**形と字の両方**に掛ける。 形だけ反転 + 漢字正立 にすると
 *     「将棋として向きがおかしい」 (= 将棋慣例違反)。
 *     ただし要素ごと rotate すると焼いた駒の**光まで回ってしまう**ので、
 *     駒形は 180° 回した別スプライト (koma-opp.png)、 漢字だけ CSS で rotate する。
 *     → ShogiKoma.tsx
 *
 * 詳細は styles.css の "Shogi board" セクションのコメント参照。
 */

import { useState } from 'react';
import {
  type DropKind,
  HAND_ORDER,
  handCount,
  handIndex,
  isPushMove,
  legalDropsFor,
  legalTargetsFor,
  moveTo,
  PIECE_KANJI,
  type PieceKind,
  SHOGI_COLS,
  SHOGI_ROWS,
  type ShogiMove,
  type ShogiState,
  type Side,
} from '../../games/shogi';
import { BoardCoords } from './BoardCoords';
import { ShogiKoma } from './ShogiKoma';

/** 成り先の駒字 (成れる駒だけ)。 ダイアログで成駒の見た目を出すため。 */
const PROMOTED_KANJI: Partial<Record<PieceKind, string>> = {
  Pawn: 'と',
  Lance: '杏',
  Knight: '圭',
  Silver: '全',
  Bishop: '馬',
  Rook: '龍',
};

function isPromotedKind(k: PieceKind): boolean {
  return k === 'Tokin' || k === 'Narikyo' || k === 'Narikei' || k === 'Narigin' || k === 'Horse' || k === 'Dragon';
}

interface Props {
  state: ShogiState;
  legal?: ShogiMove[];
  yourSide?: Side | null;
  /** 盤の向き。 省略時は `yourSide`。 */
  viewFrom?: Side;
  myTurnAllowed?: boolean;
  onMove?: (mv: ShogiMove) => void;
  mini?: boolean;
  lastMove?: ShogiMove;
  /** Replay / Archive / チュートリアル等の静的ビューでは「相手の手番」 表示を出さない。 */
  staticView?: boolean;
  /** チュートリアル用: 黄色いパルスで強調するマス。 */
  tutorialHighlight?: [number, number][];
  /** 答え/チュートリアル用: 打つ持ち駒 (DropKind 名) を駒台側で強調する。 */
  tutorialHandHighlight?: string | null;
}

type Selection =
  | { kind: 'board'; from: [number, number] }
  | { kind: 'hand'; piece: DropKind }
  | null;

export function ShogiBoard({
  state,
  legal,
  yourSide,
  viewFrom,
  myTurnAllowed,
  onMove,
  mini,
  lastMove,
  staticView,
  tutorialHighlight,
  tutorialHandHighlight,
}: Props) {
  const isMyTurn =
    myTurnAllowed !== undefined ? myTurnAllowed : yourSide != null && yourSide === state.side;
  // 「相手の手番」 hint: Reversi/Animal と同仕様。 mini / staticView (Replay/Tutorial) では出さない。
  const showOppHint = !mini && !staticView && !isMyTurn;
  const [selected, setSelected] = useState<Selection>(null);
  /** 成り選択ダイアログの内容 + どの ply 時点で開いたか。
   *  state.ply が進んだ場合、 pendingPromo は **古い局面の Move** を指しているので無効。 */
  const [pendingPromo, setPendingPromo] = useState<{
    plain: ShogiMove;
    promote: ShogiMove;
    ply: number;
  } | null>(null);

  // ply 一致しない pendingPromo は無効化 (= 描画しない、 click も無視)。
  // useEffect でクリアするとレースで「ユーザーが選んでる最中に消える」 ことがあるので、
  // **派生値 (= 計算で出す)** で判定する。 元の pendingPromo state は残してもよい。
  const activePromo =
    pendingPromo && pendingPromo.ply === state.ply ? pendingPromo : null;

  const targets: ShogiMove[] =
    selected?.kind === 'board'
      ? legalTargetsFor(legal ?? [], selected.from)
      : selected?.kind === 'hand'
        ? legalDropsFor(legal ?? [], selected.piece)
        : [];

  // 盤反転: 後手視点なら row/col を逆順に並べる。
  const flip = (viewFrom ?? yourSide) === 'Second';
  const rows: number[] = flip
    ? Array.from({ length: SHOGI_ROWS }, (_, i) => i)
    : Array.from({ length: SHOGI_ROWS }, (_, i) => SHOGI_ROWS - 1 - i);
  const cols: number[] = flip
    ? Array.from({ length: SHOGI_COLS }, (_, i) => SHOGI_COLS - 1 - i)
    : Array.from({ length: SHOGI_COLS }, (_, i) => i);

  const handleCellClick = (r: number, c: number) => {
    if (mini || !isMyTurn) return;
    // 成り選択ダイアログ表示中は他の操作を全てブロック (= モーダル動作)
    if (activePromo) return;
    const cell = state.board[r][c];

    // 持ち駒選択中の場合: 空きマスなら Drop、 そうでなければクリア
    if (selected?.kind === 'hand') {
      const drop = targets.find((m) => {
        const t = moveTo(m);
        return t[0] === r && t[1] === c;
      });
      if (drop) {
        onMove?.(drop);
        setSelected(null);
        return;
      }
      // 別の自駒を選んだなら盤選択に切替
      if (cell && cell.owner === state.side) {
        setSelected({ kind: 'board', from: [r, c] });
        return;
      }
      setSelected(null);
      return;
    }

    // 盤選択モード
    if (!selected || selected.kind !== 'board') {
      if (cell && cell.owner === state.side) {
        setSelected({ kind: 'board', from: [r, c] });
      }
      return;
    }

    // 自駒再選択
    if (cell && cell.owner === state.side) {
      const sameSquare = selected.from[0] === r && selected.from[1] === c;
      if (sameSquare) {
        setSelected(null);
        return;
      }
      setSelected({ kind: 'board', from: [r, c] });
      return;
    }

    // 目標として click
    const cands = targets.filter((m) => {
      const t = moveTo(m);
      return t[0] === r && t[1] === c;
    });
    if (cands.length === 0) {
      setSelected(null);
      return;
    }
    // 成り版・不成版両方ある場合 → ダイアログ
    const plain = cands.find((m) => isPushMove(m) && !m.Push.promote);
    const promote = cands.find((m) => isPushMove(m) && m.Push.promote);
    if (plain && promote) {
      // 「この pendingPromo は state.ply 時点で開かれた」 を記録。
      // ply が進んでいたら activePromo === null になって自動的に無効化される。
      setPendingPromo({ plain, promote, ply: state.ply });
      return;
    }
    onMove?.(cands[0]);
    setSelected(null);
  };

  const handleHandClick = (piece: DropKind, side: Side) => {
    if (mini || !isMyTurn) return;
    if (activePromo) return; // ダイアログ表示中は駒台クリックもブロック
    if (side !== state.side) return;
    if (handCount(state.hands[handIndex(side)], piece) === 0) return;
    setSelected({ kind: 'hand', piece });
  };

  // 直前手のハイライト
  const lastFrom = lastMove && isPushMove(lastMove) ? lastMove.Push.from : null;
  const lastTo = lastMove ? moveTo(lastMove) : null;

  const mySide: Side = viewFrom ?? yourSide ?? 'First';
  const oppSide: Side = mySide === 'First' ? 'Second' : 'First';
  const renderHand = (side: Side) => {
    const hand = state.hands[handIndex(side)];
    const isMyHand = yourSide === side || (yourSide == null && side === 'First');
    const total = hand.pawn + hand.lance + hand.knight + hand.silver + hand.gold + hand.bishop + hand.rook;
    return (
      <div className={`shogi-hand ${isMyHand ? 'mine' : 'theirs'}`} data-testid={`shogi-hand-${side}`}>
        {!mini && (
          <span className="shogi-hand-label">{side === 'First' ? '☗先手' : '☖後手'}</span>
        )}
        {total === 0 && !mini && <span className="shogi-hand-empty">持ち駒なし</span>}
        {HAND_ORDER.map((kind) => {
          const cnt = handCount(hand, kind);
          if (cnt === 0) return null;
          const isSelected = selected?.kind === 'hand' && selected.piece === kind && side === state.side;
          // 駒台駒も viewer から見て敵側の駒台 (= 画面上) だけ反転
          const handFlipped = side !== mySide;
          // 答えが打ち駒のとき、 自分の駒台の該当駒をパルスで示す
          const isHint = tutorialHandHighlight === kind && side === mySide;
          return (
            <button
              key={kind}
              className={`shogi-hand-piece ${isSelected ? 'selected' : ''} ${handFlipped ? 'flipped' : ''} ${
                isHint ? 'tutorial-hint' : ''
              }`}
              onClick={() => handleHandClick(kind, side)}
              disabled={mini || side !== state.side || !isMyTurn}
              aria-label={`hand ${kind} x${cnt}`}
            >
              <ShogiKoma kanji={PIECE_KANJI[kind]} flipped={handFlipped} hand />
              {cnt > 1 && <span className="count">×{cnt}</span>}
            </button>
          );
        })}
      </div>
    );
  };

  return (
    <div className={`shogi-frame ${mini ? 'mini' : ''} ${showOppHint ? 'not-my-turn' : ''}`}>
      {renderHand(oppSide)}
      <div className="shogi-board">
        {/* 座標は升の外 (木地の縁) に。 升の中だと駒に隠れる。 表示順のまま渡すので反転も追従する。 */}
        {!mini && (
          <BoardCoords
            files={cols.map((c) => `${9 - c}`)}
            ranks={rows.map((r) => ['九', '八', '七', '六', '五', '四', '三', '二', '一'][r] ?? '')}
          />
        )}
        {rows.flatMap((r) =>
          cols.map((c) => {
            const cell = state.board[r][c];
            const isSelected =
              selected?.kind === 'board' && selected.from[0] === r && selected.from[1] === c;
            const isTarget = targets.some((m) => {
              const t = moveTo(m);
              return t[0] === r && t[1] === c;
            });
            const isLast =
              !mini &&
              ((lastFrom != null && lastFrom[0] === r && lastFrom[1] === c) ||
                (lastTo != null && lastTo[0] === r && lastTo[1] === c));
            const isTutorialHint =
              tutorialHighlight?.some(([hr, hc]) => hr === r && hc === c) ?? false;
            // 駒の反転は owner 固定ではなく viewer から見て敵側 (= 画面上に表示される側)
            // の駒だけ反転 (頂点が中央向き)。 盤反転時 (yourSide='Second') にも自然な見え方になる。
            const pieceFlipped = cell != null && cell.owner !== mySide;
            return (
              <button
                key={`${r}-${c}`}
                className={`cell ${isSelected ? 'selected' : ''} ${isTarget ? 'target' : ''} ${
                  isLast ? 'last-move' : ''
                } ${isTutorialHint ? 'tutorial-hint' : ''}`}
                disabled={mini}
                onClick={() => handleCellClick(r, c)}
                aria-label={`row ${r} col ${c}`}
              >
                {cell && (
                  <ShogiKoma
                    /* 慣例: 後手の玉は「王」、 先手は「玉」 */
                    kanji={
                      cell.kind === 'King' && cell.owner === 'Second'
                        ? '王'
                        : PIECE_KANJI[cell.kind]
                    }
                    flipped={pieceFlipped}
                    promoted={isPromotedKind(cell.kind)}
                    className={cell.owner === 'First' ? 'first' : 'second'}
                  />
                )}
              </button>
            );
          }),
        )}
      </div>
      {renderHand(mySide)}
      {showOppHint && (
        <div className="board-turn-hint" aria-hidden>
          手番待ち
        </div>
      )}
      {activePromo && (() => {
        // 成/不成の二択は文字ではなく駒そのもので見せる (成駒は赤字)。
        const from = isPushMove(activePromo.plain) ? activePromo.plain.Push.from : null;
        const moving = from ? state.board[from[0]][from[1]] : null;
        const kind = moving?.kind ?? 'Pawn';
        const choose = (mv: ShogiMove) => {
          // click 時点でターンが進行していたら無視 (古いダイアログ)。
          if (isMyTurn && activePromo.ply === state.ply) onMove?.(mv);
          setPendingPromo(null);
          setSelected(null);
        };
        return (
          <div
            className="shogi-promo-overlay"
            onClick={(e) => e.stopPropagation()}
            data-testid="shogi-promo-overlay"
          >
            <div className="shogi-promo-dialog" data-testid="shogi-promo" role="dialog" aria-label="成りますか">
              <div className="shogi-promo-title">成りますか</div>
              <div className="shogi-promo-buttons">
                <button
                  type="button"
                  className="shogi-promo-btn promote"
                  onClick={() => choose(activePromo.promote)}
                  aria-label="成"
                >
                  <span className="shogi-promo-koma">
                    <ShogiKoma kanji={PROMOTED_KANJI[kind] ?? PIECE_KANJI[kind]} promoted />
                  </span>
                  <span className="shogi-promo-caption">成</span>
                </button>
                <button
                  type="button"
                  className="shogi-promo-btn plain"
                  onClick={() => choose(activePromo.plain)}
                  aria-label="不成"
                >
                  <span className="shogi-promo-koma">
                    <ShogiKoma kanji={PIECE_KANJI[kind]} />
                  </span>
                  <span className="shogi-promo-caption">不成</span>
                </button>
              </div>
            </div>
          </div>
        );
      })()}
    </div>
  );
}

